import React, { ComponentType } from 'react';
import { useAuthInDev } from 'hooks/auth';

/**
 * Returns a component that renders the wrapped component if the user is authenticated or the user is not in DEV.
 * Otherwise, renders the authentication screen where the user may sign in.
 *
 * @param WrappedComponent - Component that will be rendered when the user is authenticated or when they are not in DEV.
 */
export function withAuthInDev<T>(WrappedComponent: ComponentType<T>) {
  /**
   * Applies auth rendering logic to the wrapped component
   *
   * @param props - Original properties that would've been assigned to WrappedComponent
   */
  function ComponentWithAuth(props: T) {
    const {
      isAuthenticated,
      authenticateWithGoogle,
      authenticateWithEmailAndPassword,
      wasAuthenticationChecked,
    } = useAuthInDev();

    if (!wasAuthenticationChecked) {
      return <></>;
    }

    if (!isAuthenticated) {
      const AuthScreen = React.lazy(() => import('./index'));

      return (
        <React.Suspense fallback={<></>}>
        <AuthScreen
          authenticateWithEmailAndPassword={authenticateWithEmailAndPassword}
          authenticateWithGoogle={authenticateWithGoogle}
        />
        </React.Suspense>
      );
    }

    return <WrappedComponent {...props} />;
  }
  // @ts-ignore
  ComponentWithAuth.getInitialProps = WrappedComponent.getInitialProps;

  return ComponentWithAuth;
}
